/**
 * Watercolor Atlas header behaviors.
 * - Sticky header shadow on scroll
 * - Compact inline predictive search panel (/search/suggest.json)
 * - Mobile drawer (<dialog>) with autofocused search input
 * - Cart count badge updates via the "cart:updated" custom event
 * Vanilla JS, no dependencies, progressive enhancement only.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function formatMoney(value, format) {
    if (!value && value !== 0) return '';
    if (!format) return '$' + value;
    return format.replace(/\{\{\s*\w+\s*\}\}/g, value);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /* ------------------------------------------------------------------ */
  /* Cart count badge                                                     */
  /* ------------------------------------------------------------------ */

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      if (node.classList.contains('dw-header__cart-count')) {
        node.classList.toggle('is-empty', !count);
      }
    });
  }

  function bindGlobalListeners() {
    if (window.__dwHeaderGlobalBound) return;
    window.__dwHeaderGlobalBound = true;

    document.addEventListener('cart:updated', function (event) {
      var detail = event.detail || {};
      var count = null;
      if (typeof detail.item_count === 'number') {
        count = detail.item_count;
      } else if (detail.cart && typeof detail.cart.item_count === 'number') {
        count = detail.cart.item_count;
      }
      if (count === null) {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart.js')
          .then(function (response) { return response.json(); })
          .then(function (cart) { updateCartCount(cart.item_count); })
          .catch(function () {});
      } else {
        updateCartCount(count);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky shadow                                                        */
  /* ------------------------------------------------------------------ */

  function initSticky(header, sectionEl) {
    if (header.dataset.sticky !== 'true' || !sectionEl) return;
    var onScroll = function () {
      if (!sectionEl.isConnected) {
        window.removeEventListener('scroll', onScroll);
        return;
      }
      sectionEl.classList.toggle('dw-is-stuck', window.scrollY > 4);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Predictive search panel                                              */
  /* ------------------------------------------------------------------ */

  function buildProductItem(product, moneyFormat) {
    var link = el('a', 'dw-search-panel__item dw-search-panel__item--product');
    link.href = product.url || '#';

    var imageSrc = '';
    if (typeof product.image === 'string') {
      imageSrc = product.image;
    } else if (product.featured_image && product.featured_image.url) {
      imageSrc = product.featured_image.url;
    }
    if (imageSrc) {
      var img = document.createElement('img');
      img.className = 'dw-search-panel__thumb';
      img.src = imageSrc;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 44;
      img.height = 44;
      link.appendChild(img);
    } else {
      link.appendChild(el('span', 'dw-search-panel__thumb'));
    }

    var meta = el('span', 'dw-search-panel__meta');
    meta.appendChild(el('span', 'dw-search-panel__title', product.title || ''));
    if (product.price) {
      meta.appendChild(el('span', 'dw-search-panel__price', formatMoney(product.price, moneyFormat)));
    }
    link.appendChild(meta);
    return link;
  }

  function buildArticleItem(article) {
    var link = el('a', 'dw-search-panel__item dw-search-panel__item--article');
    link.href = article.url || '#';
    link.appendChild(el('span', 'dw-search-panel__title', article.title || ''));
    return link;
  }

  function initSearch(header) {
    var toggle = header.querySelector('[data-search-toggle]');
    var panel = header.querySelector('[data-search-panel]');
    if (!toggle || !panel) return;

    var input = panel.querySelector('[data-search-input]');
    var results = panel.querySelector('[data-search-results]');
    var viewAll = panel.querySelector('[data-search-view-all]');
    var predictiveUrl = panel.dataset.predictiveUrl || '/search/suggest';
    var searchUrl = panel.dataset.searchUrl || '/search';
    var moneyFormat = panel.dataset.moneyFormat || '';
    var controller = null;

    function openPanel() {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      if (input) {
        input.focus();
        input.select();
      }
    }

    function closePanel() {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', function () {
      if (panel.hidden) {
        openPanel();
      } else {
        closePanel();
      }
    });

    document.addEventListener('click', function (event) {
      if (panel.hidden) return;
      if (panel.contains(event.target) || toggle.contains(event.target)) return;
      closePanel();
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closePanel();
        toggle.focus();
      }
    });

    function paintResults(data, query) {
      if (!results || !viewAll) return;
      results.textContent = '';

      var resources = (data && data.resources && data.resources.results) || {};
      var products = resources.products || [];
      var articles = resources.articles || [];

      if (products.length) {
        results.appendChild(el('p', 'dw-search-panel__group-title', 'Products'));
        var productGroup = el('div', 'dw-search-panel__group');
        products.forEach(function (product) {
          productGroup.appendChild(buildProductItem(product, moneyFormat));
        });
        results.appendChild(productGroup);
      }

      if (articles.length) {
        results.appendChild(el('p', 'dw-search-panel__group-title', 'Articles'));
        var articleGroup = el('div', 'dw-search-panel__group');
        articles.forEach(function (article) {
          articleGroup.appendChild(buildArticleItem(article));
        });
        results.appendChild(articleGroup);
      }

      if (!products.length && !articles.length) {
        results.appendChild(el('p', 'dw-search-panel__empty', 'No results for "' + query + '". Try another search.'));
      }

      viewAll.href = searchUrl + '?q=' + encodeURIComponent(query);
      viewAll.hidden = false;
    }

    var runSearch = debounce(function () {
      var query = input.value.trim();
      if (query.length < 2) {
        if (results) results.textContent = '';
        if (viewAll) viewAll.hidden = true;
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();

      var url = predictiveUrl + '.json?q=' + encodeURIComponent(query) +
        '&resources[type]=product,article' +
        '&resources[limit]=5' +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
          return response.json();
        })
        .then(function (data) {
          paintResults(data, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (results) results.textContent = '';
        });
    }, 220);

    if (input) input.addEventListener('input', runSearch);
  }

  /* ------------------------------------------------------------------ */
  /* Mobile drawer                                                        */
  /* ------------------------------------------------------------------ */

  function initDrawer(header) {
    var openButton = header.querySelector('[data-drawer-open]');
    var drawer = header.querySelector('[data-drawer]');
    if (!openButton || !drawer || typeof drawer.showModal !== 'function') return;

    openButton.addEventListener('click', function () {
      drawer.showModal();
      var searchInput = drawer.querySelector('input[type="search"]');
      if (searchInput) searchInput.focus();
    });

    drawer.querySelectorAll('[data-drawer-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        drawer.close();
      });
    });

    /* Click on the ::backdrop dispatches to the dialog element itself. */
    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) drawer.close();
    });

    /* Close the drawer when a navigation link inside it is followed. */
    drawer.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[href]') : null;
      if (link) drawer.close();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function initHeader(header) {
    if (!header || header.dataset.dwInit === 'true') return;
    header.dataset.dwInit = 'true';
    var sectionEl = header.closest('.shopify-section') || header.parentElement;
    initSticky(header, sectionEl);
    initSearch(header);
    initDrawer(header);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-dw-header]').forEach(initHeader);
  }

  bindGlobalListeners();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
